import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BarChart3, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TotalAnalyticsTab = () => {
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/total?period=${period}`, { credentials: 'include' });
      if (res.ok) setAnalytics(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const handleResetAnalytics = async () => {
    if (confirmText !== "RESET") {
      toast.error("Please type RESET to confirm");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/employer/analytics/reset`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success("Analytics reset and archived");
        setShowConfirmReset(false);
        setShowResetDialog(false);
        setConfirmText("");
        fetchAnalytics();
      }
    } catch (e) { toast.error("Failed to reset"); }
  };

  const handleFirstConfirm = () => {
    setShowResetDialog(false);
    setShowConfirmReset(true);
  };

  const renderSummary = () => {
    if (!analytics) return null;
    const s = analytics.summary;
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-primary">{s.total_employees}</p><p className="text-sm text-muted-foreground">{t("totalEmployees") || "Total Employees"}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-blue-500">{s.total_hours}h</p><p className="text-sm text-muted-foreground">{t("totalHours") || "Total Hours"}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-green-500">{s.company_attendance_rate}%</p><p className="text-sm text-muted-foreground">{t("attendanceRate") || "Attendance Rate"}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-destructive">{s.total_late_days}</p><p className="text-sm text-muted-foreground">{t("lateDays") || "Late Days"}</p></CardContent></Card>
      </div>
    );
  };

  const renderEmployees = () => {
    if (!analytics || !analytics.employees) return null;
    const empList = [];
    for (let i = 0; i < analytics.employees.length; i++) {
      const emp = analytics.employees[i];
      empList.push(
        <div key={emp.user_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div>
            <p className="font-medium">{emp.name}</p>
            <p className="text-sm text-muted-foreground">{emp.email}</p>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="text-center"><p className="font-bold">{emp.days_worked}</p><p className="text-xs text-muted-foreground">{t("days") || "Days"}</p></div>
            <div className="text-center"><p className="font-bold">{emp.total_hours}h</p><p className="text-xs text-muted-foreground">{t("hours") || "Hours"}</p></div>
            <div className="text-center"><p className="font-bold text-green-600">{emp.attendance_rate}%</p><p className="text-xs text-muted-foreground">{t("rate") || "Rate"}</p></div>
            <div className="text-center"><p className="font-bold text-destructive">{emp.late_days}</p><p className="text-xs text-muted-foreground">{t("late") || "Late"}</p></div>
          </div>
        </div>
      );
    }
    return <div className="space-y-3">{empList}</div>;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                {t("totalAnalytics") || "Total Analytics"}
              </CardTitle>
              <CardDescription>{t("companyWideMetrics") || "Company-wide attendance metrics"}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowResetDialog(true)} className="text-destructive hover:text-destructive">
                <RotateCcw className="w-4 h-4 mr-1" />{t("resetAll") || "Reset All"}
              </Button>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">{t("week") || "Week"}</SelectItem>
                  <SelectItem value="month">{t("month") || "Month"}</SelectItem>
                  <SelectItem value="year">{t("year") || "Year"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-center py-8 text-muted-foreground">{t("loading") || "Loading..."}</p>}
          {!loading && analytics && (
            <>
              {renderSummary()}
              <Card>
                <CardHeader><CardTitle className="text-lg">{t("employeeBreakdown") || "Employee Breakdown"}</CardTitle></CardHeader>
                <CardContent>{renderEmployees()}</CardContent>
              </Card>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* First confirmation dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Reset All Analytics?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will clear all check-in/check-out records for all employees. The data will be archived for audit purposes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleFirstConfirm} className="bg-destructive text-destructive-foreground">Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Second confirmation dialog with text input */}
      <AlertDialog open={showConfirmReset} onOpenChange={(open) => { setShowConfirmReset(open); if (!open) setConfirmText(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Final Confirmation Required
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>You are about to permanently reset all analytics data. This action will:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Clear all check-in/check-out records</li>
                <li>Reset attendance rates to 0%</li>
                <li>Archive current data to audit history</li>
              </ul>
              <div className="pt-2">
                <Label htmlFor="confirm-reset">Type <strong>RESET</strong> to confirm:</Label>
                <Input 
                  id="confirm-reset"
                  value={confirmText} 
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="Type RESET here"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetAnalytics} 
              disabled={confirmText !== "RESET"}
              className="bg-destructive text-destructive-foreground disabled:opacity-50"
            >
              Reset Analytics
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TotalAnalyticsTab;
